import { useState } from 'react';
import { Filter } from 'lucide-react';
import GameProposalForm from './GameProposalForm';
import AvailabilityForm from './AvailabilityForm';
import Modal from './modals/Modal';
import GameCard from './GameCard';
import { Player, GameProposal, ExtendedAvailability, GameLocation } from '../types';

interface CommunityBoardProps {
  games: GameProposal[];
  availabilities: ExtendedAvailability[];
  currentUser: Player | null;
  locations: GameLocation[];
  friends: Player[];
  onJoinGame: (gameId: string, message: string) => Promise<void>;
  onGameProposal: (data: any, player: Player) => Promise<void>;
  onEditGame: (gameId: string, data: any) => Promise<void>;
  onDeleteGame: (gameId: string) => Promise<void>;
  onAddAvailability: (data: any) => Promise<void>;
  onEditAvailability: (availabilityId: string, data: any) => Promise<void>;
  onDeleteAvailability: (availabilityId: string) => Promise<void>;
  onRegisterPrompt: () => void;
  onRemovePlayer: (gameId: string, playerId: string) => Promise<void>;
  onInvitePlayer?: (gameId: string, playerId: string) => Promise<void>;
}

export default function CommunityBoard({
  games,
  availabilities,
  currentUser,
  locations,
  friends,
  onJoinGame,
  onGameProposal,
  onEditGame,
  onDeleteGame,
  onAddAvailability,
  onEditAvailability,
  onDeleteAvailability,
  onRegisterPrompt,
  onRemovePlayer,
  onInvitePlayer
}: CommunityBoardProps) {
  const [showGameForm, setShowGameForm] = useState(false);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [filters, setFilters] = useState({
    sports: [] as string[],
    locations: [] as string[],
    showFriendsOnly: false
  });

  // Filter games and availabilities based on current filters
  const filteredGames = games.filter(game => {
    // Friends filter
    if (filters.showFriendsOnly) {
      const isFriendGame = friends.some(friend =>
        game.createdBy?.id === friend.id ||
        game.players.some(player => player.id === friend.id)
      );
      if (!isFriendGame) return false;
    }

    // Sports filter
    if (filters.sports.length > 0 && !filters.sports.includes(game.sport)) {
      return false;
    }

    // Locations filter
    if (filters.locations.length > 0 && !game.locations.some(locId => filters.locations.includes(locId))) {
      return false;
    }

    return true;
  });

  const filteredAvailabilities = availabilities.filter(availability => {
    // Friends filter
    if (filters.showFriendsOnly) {
      const isFriendAvailability = friends.some(friend => availability.player.id === friend.id);
      if (!isFriendAvailability) return false;
    }

    // Sports filter
    if (filters.sports.length > 0 && !availability.sports.some(sport => filters.sports.includes(sport))) {
      return false;
    }

    // Locations filter
    if (filters.locations.length > 0 && !availability.locations.some(locId => filters.locations.includes(locId))) {
      return false;
    }

    return true;
  });

  const handleFilterChange = (filterType: 'sports' | 'locations' | 'showFriendsOnly', value?: string, checked?: boolean) => {
    if (filterType === 'showFriendsOnly') {
      setFilters(prev => ({
        ...prev,
        showFriendsOnly: !prev.showFriendsOnly
      }));
    } else if (value !== undefined && checked !== undefined) {
      setFilters(prev => ({
        ...prev,
        [filterType]: checked
          ? [...prev[filterType as 'sports' | 'locations'], value]
          : prev[filterType as 'sports' | 'locations'].filter(item => item !== value)
      }));
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Community Board</h1>
        <div className="flex gap-3">
          <button
            onClick={() => {
              if (!currentUser) {
                alert('Please log in to propose a game');
                return;
              }
              setShowGameForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <span>Propose Game</span>
          </button>
          <button
            onClick={() => {
              if (!currentUser) {
                alert('Please log in to add availability');
                return;
              }
              setShowAvailabilityForm(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <span>Add Availability</span>
          </button>
        </div>
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-9">
          {/* Games Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Proposed Games ({filteredGames.length})</h2>
            <div className="space-y-4">
              {filteredGames.map((game) => {
                try {
                  return (
                    <GameCard
                      key={game.id}
                      game={game}
                      onJoinGame={onJoinGame}
                      currentUser={currentUser}
                      onRemovePlayer={onRemovePlayer}
                      locations={locations}
                      onInvitePlayer={onInvitePlayer || (() => { })}
                      onRegisterPrompt={onRegisterPrompt}
                      onGameClick={undefined}
                      onMarkComplete={undefined}
                      onAddPlayerDirectly={undefined}
                    />
                  );
                } catch (error) {
                  console.error('Error rendering GameCard:', error, game);
                  return (
                    <div key={game.id} className="p-4 border border-red-200 bg-red-50 rounded-lg">
                      Error rendering game: {game.title || game.id}
                    </div>
                  );
                }
              })}
            </div>
          </div>

          {/* Availabilities Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Player Availabilities ({filteredAvailabilities.length})</h2>
            <div className="space-y-4">
              {filteredAvailabilities.map((availability) => (
                <div key={availability.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-lg">{availability.player.name}</h3>
                  <p className="text-gray-600 mt-2">{availability.notes}</p>
                  <div className="mt-3 text-sm text-gray-500">
                    <span>🎾 {availability.sports.join(', ')}</span>
                    <span className="ml-4">📅 {availability.timeSlots.length} time slots</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filters Sidebar */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </h3>
            <div className="space-y-4">
              {/* Friends Filter */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={filters.showFriendsOnly}
                    onChange={() => handleFilterChange('showFriendsOnly')}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Show Friends Only ({friends.length} friends)
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sports</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={filters.sports.includes('tennis')}
                      onChange={(e) => handleFilterChange('sports', 'tennis', e.target.checked)}
                    />
                    <span className="text-sm">Tennis</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={filters.sports.includes('padel')}
                      onChange={(e) => handleFilterChange('sports', 'padel', e.target.checked)}
                    />
                    <span className="text-sm">Padel</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={filters.sports.includes('beach-tennis')}
                      onChange={(e) => handleFilterChange('sports', 'beach-tennis', e.target.checked)}
                    />
                    <span className="text-sm">Beach Tennis</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
                <div className="space-y-2">
                  {locations.map((location) => (
                    <label key={location.id} className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.locations.includes(location.id)}
                        onChange={(e) => handleFilterChange('locations', location.id, e.target.checked)}
                      />
                      <span className="text-sm">{location.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Friends List */}
          {friends.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Your Friends ({friends.length})</h3>
              <div className="space-y-2">
                {friends.map((friend) => (
                  <div key={friend.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {friend.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{friend.name}</p>
                        <p className="text-xs text-gray-500">{friend.email}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {friend.preferred_sports?.join(', ') || 'No sports'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Game Proposal Modal */}
      {showGameForm && (
        <Modal isOpen={showGameForm} onClose={() => setShowGameForm(false)} title="Propose Game">
          <GameProposalForm
            onSubmit={(data) => {
              onGameProposal(data, currentUser!);
              setShowGameForm(false);
            }}
            onClose={() => setShowGameForm(false)}
            currentUser={currentUser}
            availableLocations={locations}
          />
        </Modal>
      )}

      {/* Availability Form Modal */}
      {showAvailabilityForm && (
        <Modal isOpen={showAvailabilityForm} onClose={() => setShowAvailabilityForm(false)} title="Add Availability">
          <AvailabilityForm
            onSubmit={(data) => {
              onAddAvailability(data);
              setShowAvailabilityForm(false);
            }}
            onClose={() => setShowAvailabilityForm(false)}
            availableLocations={locations}
            currentUser={currentUser}
          />
        </Modal>
      )}
    </div>
  );
}